import type { ProjectMeta, ProjectSortMode } from "@supermanus/local-store";
import {
  loadProjectCustomOrder,
  loadProjectSortMode,
  mergeProjectCustomOrder,
  saveProjectCustomOrder,
  saveProjectSortMode,
  sortProjectList,
} from "@supermanus/local-store";
import {
  reorderByBefore,
  reorderDropClass,
  usePointerReorder,
} from "@supermanus/editor";
import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
  type Ref,
} from "react";

export interface ProductionHomeProps {
  projects: ProjectMeta[];
  userId: string;
  busy?: boolean;
  unlocked: boolean;
  onOpen: (id: string) => void;
  onCreate: (title: string) => void;
  onDelete: (id: string) => void;
  licenseKey: string;
  licenseStatus: string;
  licenseMessage?: string | null;
  needsName?: boolean;
  claimName?: string;
  onLicenseKeyChange: (key: string) => void;
  onClaimNameChange?: (name: string) => void;
  onCheckLicense: () => void;
  onCancelName?: () => void;
  shareError?: string | null;
}

function formatUpdatedAt(iso: string): string {
  try {
    return new Date(iso).toLocaleString("da-DK", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

const LICENSE_STATUS_LABEL: Record<string, string> = {
  ok: "Gyldig",
  grace: "Respit",
  readonly: "Skrivebeskyttet",
};

const LICENSE_MESSAGE_DA: Record<string, string> = {
  "Invalid license key.": "Ugyldig licensnøgle.",
  "License active.": "Licens aktiv.",
  "No license. Enter a license key when online.": "Indtast en licensnøgle.",
  "License check failed and no valid cached license.":
    "Licensen kunne ikke bekræftes.",
  "Name required to activate this license.":
    "Indtast dit navn for at aktivere licensen.",
};

const SORT_OPTIONS: { value: ProjectSortMode; label: string }[] = [
  { value: "alpha", label: "Alfabetisk" },
  { value: "created", label: "Senest oprettet" },
  { value: "updated", label: "Senest ændret" },
  { value: "custom", label: "Brugerdefineret" },
];

function licenseMessageDa(message: string | null | undefined): string | null {
  if (!message) return null;
  if (LICENSE_MESSAGE_DA[message]) return LICENSE_MESSAGE_DA[message];
  if (message.startsWith("License active.")) {
    return "Licens aktiv. (kunne ikke nå licensserveren)";
  }
  return message;
}

function metaLine(p: ProjectMeta, mode: ProjectSortMode): string {
  if (mode === "created") {
    return `Oprettet ${formatUpdatedAt(p.createdAt || p.updatedAt)}`;
  }
  return `Ændret ${formatUpdatedAt(p.updatedAt)}`;
}

function LicenseForm({
  licenseKey,
  licenseStatus,
  licenseMessage,
  busy,
  unlocked,
  needsName,
  claimName,
  onLicenseKeyChange,
  onClaimNameChange,
  onCheckLicense,
  onCancelName,
}: {
  licenseKey: string;
  licenseStatus: string;
  licenseMessage?: string | null;
  busy?: boolean;
  unlocked: boolean;
  needsName?: boolean;
  claimName?: string;
  onLicenseKeyChange: (key: string) => void;
  onClaimNameChange?: (name: string) => void;
  onCheckLicense: () => void;
  onCancelName?: () => void;
}) {
  const translated = licenseMessageDa(licenseMessage);
  const showError = !unlocked && Boolean(translated) && !needsName;
  const canSubmit = needsName
    ? Boolean(licenseKey.trim() && (claimName ?? "").trim())
    : Boolean(licenseKey.trim());

  return (
    <form
      className="sm-home-license-form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onCheckLicense();
      }}
    >
      <label className="sm-home-create-label" htmlFor="sm-license-key">
        Licensnøgle
      </label>
      <div className="sm-home-create-row">
        <input
          id="sm-license-key"
          value={licenseKey}
          onChange={(e) => onLicenseKeyChange(e.target.value)}
          placeholder="Indtast licensnøgle"
          disabled={busy || needsName}
          autoComplete="off"
          spellCheck={false}
          autoFocus={!unlocked && !needsName}
        />
        {!needsName ? (
          <button type="submit" disabled={busy || !canSubmit}>
            {unlocked ? "Tjek licens" : "Log ind"}
          </button>
        ) : null}
      </div>
      {needsName ? (
        <>
          <label className="sm-home-create-label" htmlFor="sm-license-name">
            Dit navn
          </label>
          <p className="sm-home-license-status">
            Første gang med denne nøgle. Navnet vises ved kommentarer og
            samarbejde.
          </p>
          <div className="sm-home-create-row">
            <input
              id="sm-license-name"
              value={claimName ?? ""}
              onChange={(e) => onClaimNameChange?.(e.target.value)}
              placeholder="Fulde navn"
              disabled={busy}
              autoComplete="name"
              autoFocus
            />
            <button type="submit" disabled={busy || !canSubmit}>
              Fortsæt
            </button>
          </div>
          <button
            type="button"
            className="sm-home-license-back"
            disabled={busy}
            onClick={() => onCancelName?.()}
          >
            Brug en anden nøgle
          </button>
        </>
      ) : null}
      {showError ? (
        <p className="sm-home-license-status is-error" role="alert">
          {translated}
        </p>
      ) : unlocked ? (
        <p className="sm-home-license-status">
          Status:{" "}
          <strong>
            {LICENSE_STATUS_LABEL[licenseStatus] ?? licenseStatus}
          </strong>
        </p>
      ) : null}
    </form>
  );
}

function SortableProjectList({
  projects,
  busy,
  sortMode,
  onOpen,
  onDelete,
  onReorder,
}: {
  projects: ProjectMeta[];
  busy?: boolean;
  sortMode: ProjectSortMode;
  onOpen: (id: string) => void;
  onDelete: (id: string) => void;
  onReorder: (orderedIds: string[]) => void;
}) {
  const custom = sortMode === "custom" && projects.length > 1;
  const { listRef, dragId, dropBeforeId, beginDrag } = usePointerReorder({
    readOnly: !custom || Boolean(busy),
    axis: "y",
    onMove: (id, beforeId) => {
      onReorder(reorderByBefore(projects, id, beforeId).map((p) => p.id));
    },
  });

  return (
    <ul
      className={`sm-home-list${dragId ? " is-reordering" : ""}`}
      ref={listRef as Ref<HTMLUListElement>}
    >
      {projects.map((p, i) => {
        const isLast = i === projects.length - 1;
        const drop = custom
          ? reorderDropClass(dragId, dropBeforeId, p.id, isLast)
          : "";
        return (
          <li
            key={p.id}
            data-step-id={p.id}
            className={`sm-home-item${dragId === p.id ? " is-dragging" : ""}${drop}`}
          >
            {custom ? (
              <button
                type="button"
                className="sm-home-grip"
                aria-label="Flyt produktion"
                title="Træk for at ændre rækkefølge"
                disabled={busy}
                onPointerDown={(e: ReactPointerEvent<HTMLElement>) =>
                  beginDrag(p.id, e)
                }
              >
                ⋮⋮
              </button>
            ) : null}
            <button
              type="button"
              className="sm-home-open"
              disabled={busy}
              onClick={() => onOpen(p.id)}
            >
              <span className="sm-home-item-title">
                {p.title || "Unavngiven produktion"}
              </span>
              <span className="sm-home-item-meta">{metaLine(p, sortMode)}</span>
            </button>
            <button
              type="button"
              className="sm-home-delete"
              disabled={busy}
              title="Slet produktion"
              aria-label={`Slet ${p.title || "produktion"}`}
              onClick={() => {
                const name = p.title || "Unavngiven produktion";
                if (
                  !window.confirm(
                    `Vil du slette produktionen «${name}»? Det kan ikke fortrydes.`,
                  )
                ) {
                  return;
                }
                onDelete(p.id);
              }}
            >
              Slet
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function ProductionHome({
  projects,
  userId,
  busy,
  unlocked,
  onOpen,
  onCreate,
  onDelete,
  licenseKey,
  licenseStatus,
  licenseMessage,
  needsName,
  claimName,
  onLicenseKeyChange,
  onClaimNameChange,
  onCheckLicense,
  onCancelName,
  shareError,
}: ProductionHomeProps) {
  const [title, setTitle] = useState("");
  const [sortMode, setSortMode] = useState<ProjectSortMode>(() =>
    loadProjectSortMode(userId),
  );
  const [customOrder, setCustomOrder] = useState<string[]>(() =>
    loadProjectCustomOrder(userId),
  );

  useEffect(() => {
    setSortMode(loadProjectSortMode(userId));
    setCustomOrder(loadProjectCustomOrder(userId));
  }, [userId]);

  useEffect(() => {
    const ids = projects.map((p) => p.id);
    setCustomOrder((prev) => {
      const next = mergeProjectCustomOrder(prev, ids);
      if (
        next.length === prev.length &&
        next.every((id, i) => id === prev[i])
      ) {
        return prev;
      }
      saveProjectCustomOrder(userId, next);
      return next;
    });
  }, [projects, userId]);

  const sorted = useMemo(
    () => sortProjectList(projects, sortMode, customOrder),
    [projects, sortMode, customOrder],
  );

  function changeSortMode(mode: ProjectSortMode) {
    setSortMode(mode);
    saveProjectSortMode(userId, mode);
    if (mode !== "custom") return;
    const known = customOrder.filter((id) =>
      projects.some((p) => p.id === id),
    );
    const order =
      known.length > 0
        ? mergeProjectCustomOrder(
            known,
            projects.map((p) => p.id),
          )
        : sortProjectList(projects, "updated", []).map((p) => p.id);
    setCustomOrder(order);
    saveProjectCustomOrder(userId, order);
  }

  function handleReorder(orderedIds: string[]) {
    setCustomOrder(orderedIds);
    saveProjectCustomOrder(userId, orderedIds);
  }

  function submitCreate(e: FormEvent) {
    e.preventDefault();
    const next = title.trim() || "Unavngiven produktion";
    onCreate(next);
    setTitle("");
  }

  if (!unlocked) {
    return (
      <div className="sm-home sm-home-login">
        <header className="sm-home-hero">
          <p className="sm-home-brand">Minproduktion.dk</p>
          <h1 className="sm-home-title">Log ind</h1>
          <p className="sm-home-lead">
            {needsName
              ? "Indtast dit navn for at knytte det til licensnøglen."
              : "Indtast din licensnøgle for at se og redigere produktioner."}
          </p>
        </header>
        <section className="sm-home-license is-gate" aria-label="Licens">
          <LicenseForm
            licenseKey={licenseKey}
            licenseStatus={licenseStatus}
            licenseMessage={licenseMessage}
            busy={busy}
            unlocked={false}
            needsName={needsName}
            claimName={claimName}
            onLicenseKeyChange={onLicenseKeyChange}
            onClaimNameChange={onClaimNameChange}
            onCheckLicense={onCheckLicense}
            onCancelName={onCancelName}
          />
        </section>
      </div>
    );
  }

  return (
    <div className="sm-home">
      <header className="sm-home-hero">
        <p className="sm-home-brand">Minproduktion.dk</p>
        <h1 className="sm-home-title">Produktioner</h1>
        <p className="sm-home-lead">
          Åbn en eksisterende produktion, eller opret en ny. Ændringer gemmes
          på serveren.
        </p>
      </header>

      <section className="sm-home-list-section" aria-label="Eksisterende produktioner">
        <div className="sm-home-list-head">
          <h2 className="sm-home-list-heading">
            {projects.length === 0
              ? "Ingen produktioner endnu"
              : `${projects.length} produktion${projects.length === 1 ? "" : "er"}`}
          </h2>
          {projects.length > 0 ? (
            <label className="sm-home-sort">
              <span className="sm-home-sort-label">Sortér</span>
              <select
                value={sortMode}
                disabled={busy}
                aria-label="Sortér produktioner"
                onChange={(e) =>
                  changeSortMode(e.target.value as ProjectSortMode)
                }
              >
                {SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
        {sortMode === "custom" && projects.length > 1 ? (
          <p className="sm-home-sort-hint">
            Træk i ⋮⋮ for at ændre rækkefølgen.
          </p>
        ) : null}
        {shareError ? (
          <p className="sm-home-share-error" role="alert">
            {shareError}
          </p>
        ) : null}
        {projects.length === 0 ? (
          <p className="sm-home-empty">
            Opret den første produktion nedenfor for at komme i gang.
          </p>
        ) : (
          <SortableProjectList
            projects={sorted}
            busy={busy}
            sortMode={sortMode}
            onOpen={onOpen}
            onDelete={onDelete}
            onReorder={handleReorder}
          />
        )}
      </section>

      <form className="sm-home-create" onSubmit={submitCreate}>
        <label className="sm-home-create-label" htmlFor="sm-new-production">
          Ny produktion
        </label>
        <div className="sm-home-create-row">
          <input
            id="sm-new-production"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titel på produktion"
            disabled={busy}
            autoFocus={projects.length === 0}
          />
          <button type="submit" disabled={busy}>
            Opret
          </button>
        </div>
      </form>

      <section className="sm-home-license" aria-label="Licens">
        <h2 className="sm-home-list-heading">Licens</h2>
        <LicenseForm
          licenseKey={licenseKey}
          licenseStatus={licenseStatus}
          licenseMessage={licenseMessage}
          busy={busy}
          unlocked
          onLicenseKeyChange={onLicenseKeyChange}
          onCheckLicense={onCheckLicense}
        />
      </section>
    </div>
  );
}
